import currencySymbols from 'constants/currencies';
import { statusShortCode } from 'constants/payment';
import { QUOTATION_STATUSES } from 'constants/quoteConfirmationStatuses';
import { bookingStatus } from 'types/booking';

export function getPriceWithCurrency(value, currency) {
  const symbol = currencySymbols[currency];
  let price = symbol + value;
  const decimalIndex = price.indexOf('.');
  if (decimalIndex === -1) return price + '.00';
  if (decimalIndex === price.length - 2) return price + '0';
  return price;
}

function getBreakdownValues(booking) {
  const outbound = booking.outbound;
  const inbound = booking.inbound;
  const breakdown = {};

  [...outbound, ...inbound]?.map((leg) =>
    leg.products
      .filter((p) => !p.cancelled)
      .map((l) => {
        const paxType = l.passenger.disabilityType ? `${l.passenger.type},${l.passenger.disabilityType}` : `${l.passenger.type}`;
        return !breakdown[paxType]
          ? (breakdown[paxType] = { [l.priceWithCurrency]: 1 })
          : !breakdown[paxType][l.priceWithCurrency]
          ? (breakdown[paxType][l.priceWithCurrency] = 1)
          : (breakdown[paxType][l.priceWithCurrency] += 1);
      })
  );

  return breakdown;
}

function getPassengerType(paxString, intl) {
  const intlIds = ['adult', 'child', 'senior', 'youth', 'wheelchair', 'companion', 'guideDog'];
  const types = getIntlStrings('quoteConfirmation.breakdown.', intlIds, intl);
  const [type, disability] = paxString.split(',');
  if (type === 'AD') {
    if (disability === 'NWC') return types.companion;
    if (disability === 'COMP') return types.companion;
    if (disability === 'WC') return `${types.adult} - ${types.wheelchair}`;
    return types.adult;
  }
  if (type === 'YTH') {
    if (disability === 'NWC') return types.companion;
    if (disability === 'COMP') return types.companion;
    if (disability === 'WC') return `${types.youth} - ${types.wheelchair}`;
    return types.youth;
  }
  if (type === 'SNR') {
    if (disability === 'NWC') return types.companion;
    if (disability === 'COMP') return types.companion;
    if (disability === 'WC') return `${types.senior} - ${types.wheelchair}`;
    return types.senior;
  }
  if (type === 'CH') {
    if (disability === 'WC') return `${types.child} - ${types.wheelchair}`;
    return types.child;
  }
  if (type === 'DOG') return types.guideDog;
  return types.adult;
}

function getIntlStrings(rootId, intlIds, intl) {
  return intlIds.reduce((acc, cur) => {
    acc[cur] = intl.formatMessage({ id: `${rootId}${cur}` });
    return acc;
  }, {});
}

export function createBreakdownTableData(booking, intl) {
  const breakdown = getBreakdownValues(booking);

  const intlIds = ['pricePerPerson', 'total', 'quantity', 'passengerType'];
  const intlStrings = getIntlStrings('quoteConfirmation.breakdown.', intlIds, intl);

  const columns = [
    { key: intlStrings.passengerType },
    { key: intlStrings.quantity },
    { key: intlStrings.pricePerPerson },
    { key: intlStrings.total }
  ];

  let rows = Object.keys(breakdown).map((passengerType) =>
    Object.keys(breakdown[passengerType]).map((ticketPrice) => {
      const quantity = breakdown[passengerType][ticketPrice];
      const total = ticketPrice * quantity;
      const type = getPassengerType(passengerType, intl);
      return {
        [intlStrings.passengerType]: type,
        [intlStrings.quantity]: quantity,
        [intlStrings.pricePerPerson]: ticketPrice,
        [intlStrings.total]: total
      };
    })
  );

  rows.map((row) =>
    row.sort((a, b) => {
      const priceA = a[intlStrings.pricePerPerson];
      const priceB = b[intlStrings.pricePerPerson];
      return priceA - priceB;
    })
  );

  rows = rows.flat();

  rows = rows.map((row) => ({
    ...row,
    [intlStrings.pricePerPerson]: getPriceWithCurrency(row[intlStrings.pricePerPerson], booking.currency),
    [intlStrings.total]: getPriceWithCurrency(row[intlStrings.total], booking.currency)
  }));

  return { rows, columns };
}

export const getQuoteConfirmationStatus = (status = '', payments = []) => {
  const isOnHoldBooking = status === bookingStatus.ON_HOLD;
  const isTemporarilyOnHoldBooking = status === bookingStatus.TEMPORARILY_ON_HOLD;

  const deposit =
    payments?.reduce?.((acc, { paymentStatus, amountWithCurrency }) => {
      if (paymentStatus === statusShortCode.success) {
        return acc + amountWithCurrency;
      }
      return acc;
    }, 0) ?? 0;

  if (isTemporarilyOnHoldBooking) {
    return QUOTATION_STATUSES.TEMPORARILY_ON_HOLD;
  }

  if (isOnHoldBooking && deposit === 0) {
    return QUOTATION_STATUSES.ON_HOLD_WITHOUT_DEPOSIT;
  }

  if (isOnHoldBooking && deposit > 0) {
    return QUOTATION_STATUSES.ON_HOLD_WITH_DEPOSIT;
  }

  return;
};
